import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsApplySupervisorCheck,
  IsLoadingReviews,
  IsReviewsLoaded,
  IsShowingActives,
  loadReviewsInfo,
  loadCostCentersOwned,
  SelectCostCenterOwnedIds,
  SelectOrderedByReviewsList,
  setIsLoadingReviews,
  SelectAllCostCenter,
  loadAllCostCenters,
  IsLoadedAllCostCenters,
  IsLoadingAllCostCenters,
  setIsLoadingAllCostCenters,
} from "../redux/reducers/clientRelationshipReviewsList.reducer";
import {
  isSupervisor,
  verifyModuleAccess,
} from "../../SharedModule/utils/accessUtils";
import {
  ROLE_CLIENT_ADMIN,
  ROLE_SUPER_ADMIN,
} from "../../SharedModule/utils/constants";
import { UsersService } from "../../UsersModule/services/users";
import { SelectCostCentersOwned } from "../../UsersModule/redux/reducers/usersList.reducer";
import {
  IsClientsLoaded,
  IsClientsLoading,
  load,
  SelectOrderedBy,
  setIsClientsLoading,
} from "../redux/reducers/clientsList.reducer";

export const useClientRelationshipReviewList = () => {
  const dispatch = useDispatch();

  const { rolesLoaded, userRolesList, activeUserEmail } =
    useSelector(selectUserRoles);
  const { reviewsList: clientRelationshipReview } = useSelector(
    SelectOrderedByReviewsList
  );
  const isLoadingReviews = useSelector(IsLoadingReviews);
  const reviewsLoaded = useSelector(IsReviewsLoaded);
  const isShowingActives = useSelector(IsShowingActives);
  const isApplySupervisorCheck = useSelector(IsApplySupervisorCheck);
  const costCenterOwnedIds = useSelector(SelectCostCenterOwnedIds);
  const costCentersOwned = useSelector(SelectCostCentersOwned);

  const isSupervisorInfo = isSupervisor(userRolesList);
  const notSuperOrClientAdmin = !verifyModuleAccess(
    true,
    [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
    userRolesList
  );

  const isClientsLoading = useSelector(IsClientsLoading);
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const { clientlist: clients } = useSelector(SelectOrderedBy);
  const allCostCenters = useSelector(SelectAllCostCenter);
  const isLoadedAllCostCenters = useSelector(IsLoadedAllCostCenters);
  const isLoadingAllCostCenters = useSelector(IsLoadingAllCostCenters);

  const loadPageReviewInfo = useCallback(async () => {
    if (rolesLoaded) {
      // Load info when user is supervisor
      if (isSupervisorInfo && notSuperOrClientAdmin && !costCentersOwned) {
        let costCentersOwnedResponse;
        if (!costCentersOwned) {
          costCentersOwnedResponse = await UsersService.getCostCentersOwned(
            activeUserEmail
          );
        }
        dispatch(
          loadCostCentersOwned({
            costCentersOwned: costCentersOwned || costCentersOwnedResponse,
            applySupervisorCheck: true,
          })
        );
      }
      // load clients for modal
      if (!isClientsLoaded) {
        dispatch(setIsClientsLoading(true));
        const clientsInfo = await ClientsService.getClients();
        dispatch(load(clientsInfo));
      }
      // load cost centers isLoadedAllCostCenters
      if (!isLoadedAllCostCenters) {
        dispatch(setIsLoadingAllCostCenters(true));
        const allCostCenters = await UsersService.getCostCenters();
        dispatch(loadAllCostCenters(allCostCenters));
      }
    }
  }, [dispatch, rolesLoaded, isClientsLoaded, isLoadedAllCostCenters, isSupervisorInfo, costCentersOwned]);

  const loadReviews = useCallback(
    async (needReviewRefresh = false) => {
      if (
        rolesLoaded &&
        isClientsLoaded &&
        (!reviewsLoaded || (reviewsLoaded && needReviewRefresh))
      ) {
        dispatch(setIsLoadingReviews(true));
        const reviewsInfo = await ClientsService.getClientRelationshipReviews();
        dispatch(loadReviewsInfo({ data: reviewsInfo.data, clients: clients }));
      }
    },
    [dispatch, rolesLoaded, isClientsLoaded, reviewsLoaded]
  );

  useEffect(() => {
    loadPageReviewInfo();
    loadReviews();
  }, [loadReviews]);

  return {
    clientRelationshipReview,
    isLoadingReviews,
    isShowingActives,
    isApplySupervisorCheck,
    costCenterOwnedIds,
    rolesLoaded,
    userRolesList,
    loadReviews,
    setIsLoadingReviews,
    isClientsLoading,
    clients,
    isLoadingAllCostCenters,
    allCostCenters,
  };
};
